<div class="mb-8 form-section" [class.drawer-open]="chatOpen">

  <!-- Top Bar -->
  <div class="w-full flex flex-row justify-between items-center mt-8 mb-4">
    <app-btn-back routeTo=".." icon="fa fa-close" btnText="Cancel"></app-btn-back>
    <button type="button" (click)="showPreviewModal = true"
      class="flex items-center gap-2 text-xs font-semibold text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-2xs transition-all">
      <i class="fa fa-mobile-screen"></i> Preview
    </button>
  </div>

  <!-- Title Row -->
  <div class="mb-4 border-0 w-full">
    <h1 class="font-semibold text-2xl capitalize text-primary-500">
      {{ noticeData?.id ? 'Update ' : 'New ' }} Notice
    </h1>
  </div>

  <!-- Notice Details / Notice Image tabs — the image editor is a tab on this SAME component
       instance rather than a modal, so switching back and forth never loses form or chat state. -->
  <div class="mb-5 border-b border-gray-200">
    <div class="flex gap-1">
      <button type="button" (click)="activeTab = 'form'"
        [class]="activeTab === 'form' ? 'text-primary-600 border-primary-500' : 'text-gray-500 border-transparent hover:text-gray-700'"
        class="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px">
        <i class="fa fa-pen-to-square"></i> Notice Details
      </button>
      <button type="button" (click)="openImageEditor()"
        [class]="activeTab === 'image' ? 'text-primary-600 border-primary-500' : 'text-gray-500 border-transparent hover:text-gray-700'"
        class="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px">
        <i class="fa fa-image"></i> Notice Image
        @if (currentNoticeImage) {
        <span class="w-1.5 h-1.5 rounded-full bg-green-500"></span>
        }
      </button>
    </div>
  </div>

  <!-- NOTICE IMAGE TAB — mounted once on first use (tab click, or an AI background auto-generation)
       and never torn down again after that, just hidden via CSS, so switching back to Notice Details
       and returning never resets in-progress, unsaved layer edits on the canvas. -->
  @if (imageEditorMounted) {
  <div class="mb-6 h-[75vh] min-h-[560px]" [class.hidden]="activeTab !== 'image'">
    <app-image-editor #imageEditor [groupId]="groupId || ''" contextLabel="notice" aspectRatio="3:4"
      [previewImageUrl]="currentNoticeImage || undefined" [preSignedUrlEndpoint]="preSignedUrlEndpoint"
      [s3ApiKey]="apiKey" [cloudFrontDomain]="cloudFrontDomain" [uploadMetadata]="metadata"
      (saved)="onImageEditorSaved($event)" (cancelled)="onImageEditorCancelled()"></app-image-editor>
  </div>
  }

  <!-- NOTICE DETAILS TAB -->
  <form [formGroup]="pageForm" (ngSubmit)="upsert()" [class.hidden]="activeTab !== 'form'">

    <!-- Notice Image Card -->
    <div class="mb-5 p-4 lg:p-3 xl:p-4 bg-gray-50/50 border border-gray-200/60 rounded-xl">
      <label class="block uppercase text-gray-500 text-xs font-bold mb-3">
        Notice Image
      </label>
      <div class="flex flex-row items-start gap-3">
        <button type="button" (click)="openImageEditor()"
          [title]="currentNoticeImage ? 'Click to edit image' : 'Click to open the image editor'"
          class="group relative h-[84px] w-[84px] shrink-0 rounded-xl overflow-hidden border border-gray-300 shadow-sm bg-gray-100 flex items-center justify-center cursor-pointer">
          @if (currentNoticeImage) {
          <img [src]="currentNoticeImage" alt="Notice Image Thumbnail" class="w-full h-full object-cover" />
          <div
            class="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-all">
            <i class="fa fa-pen text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity"></i>
          </div>
          } @else {
          <i class="fa fa-image text-gray-300 text-xl group-hover:text-gray-400 transition-colors"></i>
          }
        </button>

        <div class="flex-1 min-w-0">
          <p class="text-xs text-gray-500 mb-2">
            {{ currentNoticeImage
            ? 'Click the image, or the button below, to fine-tune the text, layout, or background.'
            : 'Ask the AI assistant, or open the editor to build one from scratch.' }}
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <button type="button" (click)="openImageEditor()"
              class="flex items-center gap-1.5 px-3.5 py-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-xs rounded-lg transition-all shadow-2xs">
              <i class="fa fa-wand-magic-sparkles"></i>
              {{ currentNoticeImage ? 'Edit image' : 'Open image editor' }}
            </button>
            @if (currentNoticeImage) {
            <button type="button" (click)="currentNoticeImage = null"
              class="flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-red-500 hover:bg-red-50 font-semibold text-xs rounded-lg transition-all">
              <i class="fa fa-trash"></i> Remove
            </button>
            }
          </div>

          <details class="mt-2 text-xs">
            <summary class="cursor-pointer text-gray-500 hover:text-gray-700 select-none">Or upload your own
              image instead</summary>
            <div class="mt-2 max-w-xs">
              <app-s3-image-upload #imageUploader (uploadStatus)="onImageUploadStatus($event)"
                [preSignedUrlEndpoint]="preSignedUrlEndpoint" [showLabels]="false" [apikey]="apiKey"
                [metadata]="metadata" [cloudFrontDomain]="cloudFrontDomain"></app-s3-image-upload>
            </div>
          </details>
        </div>
      </div>
    </div>

    <!-- Title -->
    <div class="w-full mb-4">
      <label class="block uppercase text-gray-500 text-xs font-bold mb-1">
        Title (100 Characters) *
      </label>
      <input type="text" formControlName="title" id="title" name="title" required
        class="w-full bg-white text-gray-800 border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
      <div
        class="{{ pageForm.get('title')?.invalid && (pageForm.get('title')?.dirty || pageForm.get('title')?.touched) ? 'visible' : 'invisible'}} pt-1 h-5 text-red-500 text-xs">
        @if (pageForm.get('title')?.errors?.['minlength'] || pageForm.get('title')?.errors?.['maxlength']) {
        <p>Min {{ pageForm.get('title')?.errors?.['minlength']?.requiredLength }} and max {{
          pageForm.get('title')?.errors?.['maxlength']?.requiredLength }} characters required</p>
        }
      </div>
    </div>

    <!-- Description -->
    <div class="w-full mb-4">
      <label class="block uppercase text-gray-500 text-xs font-bold mb-1">
        Description (5000 Characters) *
      </label>
      <textarea name="description" id="description" formControlName="description" placeholder="Description" required
        rows="5"
        class="w-full bg-white text-gray-800 border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary-500 whitespace-pre-line"></textarea>
      <div
        class="{{ pageForm.get('description')?.invalid && (pageForm.get('description')?.dirty || pageForm.get('description')?.touched) ? 'visible' : 'invisible'}} pt-1 h-5 text-red-500 text-xs">
        @if (pageForm.get('description')?.errors?.['minlength'] ||
        pageForm.get('description')?.errors?.['maxlength']) {
        <p>Min {{ pageForm.get('description')?.errors?.['minlength']?.requiredLength }} and max {{
          pageForm.get('description')?.errors?.['maxlength']?.requiredLength }} characters required</p>
        }
      </div>
    </div>

    <!-- Publish Dates Row -->
    <div class="flex flex-col md:flex-row w-full gap-4 mb-6">
      <div class="flex flex-col w-full">
        <label class="block uppercase text-gray-500 text-xs font-bold mb-1">
          Publish On *
        </label>
        <input type="datetime-local" name="publishOn" id="publishOn" formControlName="publishOn"
          class="w-full bg-white text-gray-800 border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
      </div>
      <div class="flex flex-col w-full">
        <label class="block uppercase text-gray-500 text-xs font-bold mb-1">
          Publish Till *
        </label>
        <input type="datetime-local" name="removeAfter" id="removeAfter" formControlName="removeAfter"
          class="w-full bg-white text-gray-800 border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
      </div>
    </div>

    @if (myRoles?.management) {
    <div class="w-full mb-4 flex flex-row flex-wrap gap-1">
      <app-checkbox formControlName="isGradeNotice" (change)="onGradeNotice()" label="Is Grade Notice?"></app-checkbox>
    </div>
    }

    <!-- Attachments Section -->
    <div class="w-full mb-6 p-5 lg:p-4 xl:p-5 bg-gray-50/50 border border-gray-200/60 rounded-xl">
      <div class="mb-4 pb-2 border-b border-gray-200/50">
        <div class="flex justify-between items-center">
          <label class="text-xs font-bold uppercase text-gray-600">
            Attachments (Max 5, up to 5 MB each)
          </label>
          <button type="button" (click)="fileInput.click()"
            [disabled]="working || (existingAttachments.length + newAttachments.length >= 5)"
            class="text-xs font-bold text-primary-500 hover:text-primary-600 disabled:text-gray-400 flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg shadow-2xs border border-gray-200 transition-all">
            <i class="fa fa-paperclip"></i> Add Files
          </button>
          <input #fileInput type="file" (change)="onFileSelected($event)" multiple class="hidden" />
        </div>
      </div>

      <!-- Existing Attachments -->
      @if (existingAttachments.length > 0) {
      <div class="mb-4 space-y-2">
        <p class="text-xs font-semibold text-gray-500 mb-1">Uploaded Attachments</p>
        @for (attachment of existingAttachments; track attachment.name; let i = $index) {
        <div class="flex items-center justify-between p-2.5 bg-white border border-gray-200 rounded-lg shadow-2xs">
          <div class="flex items-center gap-2 overflow-hidden mr-2">
            <i
              [class]="attachment.url.toLowerCase().endsWith('.pdf') ? 'far fa-file-pdf text-primary-500' : 'far fa-file-lines text-primary-500'"></i>
            <span class="text-sm text-gray-700 truncate" [title]="attachment.name">{{
              attachment.name }}</span>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <a [href]="attachment.url" target="_blank" class="text-xs text-primary-500 hover:underline mr-2">View</a>
            <app-btn-fab (click)="deleteExistingAttachment(attachment, i)" [isDisabled]="working" icon="fa fa-trash-can"
              textColor="text-red-500 disabled:text-gray-200" tooltip="Delete">
            </app-btn-fab>
          </div>
        </div>
        }
      </div>
      }

      <!-- New Attachments -->
      @if (newAttachments.length > 0) {
      <div class="space-y-2">
        <p class="text-xs font-semibold text-gray-500 mb-1">New Attachments to Upload</p>
        @for (item of newAttachments; track item.name; let i = $index) {
        <div class="flex flex-col p-2.5 bg-white border border-gray-200 rounded-lg shadow-2xs">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2 overflow-hidden mr-2">
              <i
                [class]="item.name.toLowerCase().endsWith('.pdf') ? 'far fa-file-pdf text-gray-400' : 'far fa-file-lines text-gray-400'"></i>
              <span class="text-sm text-gray-700 truncate" [title]="item.name">{{ item.name }}</span>
            </div>
            <button type="button" (click)="removeNewAttachment(i)" [disabled]="working || item.uploading"
              class="text-red-500 hover:text-red-700 disabled:opacity-50">
              <i class="fa fa-close"></i>
            </button>
          </div>
          @if (item.uploading) {
          <div class="w-full bg-gray-200 rounded-full h-1.5 mt-2">
            <div class="bg-primary-500 h-1.5 rounded-full transition-all duration-300" [style.width.%]="item.progress">
            </div>
          </div>
          }
        </div>
        }
      </div>
      }

      @if (existingAttachments.length === 0 && newAttachments.length === 0) {
      <div class="text-center py-4 text-xs text-gray-400">
        No attachments added.
      </div>
      }
    </div>

    <!-- Audience / Notice For -->
    @if (group?.groupType === 'school') {
    <div class="w-full mb-4">
      <label class="block uppercase text-gray-500 text-xs font-bold mb-1">
        Notice For *
      </label>
      @if(canSelectNoticeAudience() && !pageForm.get('isGradeNotice')?.value) {
      <div class="w-full flex flex-row flex-wrap gap-1">
        @for (item of memberTypesList; track item) {
        <app-btn-chip class="mb-1" [label]="item" (click)="onMemberTypeChange(item)" [readOnly]="false"
          bgColor="bg-gray-200" [selected]="pageForm.get('memberType')?.value.includes(item)"></app-btn-chip>
        }
      </div>
      } @else {
      @if(myRoles?.management) {
      <select (change)="onChangeGradeOption($event)"
        class="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm shadow-2xs w-full text-gray-800">
        @for (item of (grades$ | async); track item.id) {
        <option [ngValue]="item" [selected]="pageForm.get('gradeId')?.value?.id == item.id" [value]="item.id">
          {{item.gradeName}}
        </option>
        }
      </select>
      } @else if (myRoles?.faculty) {
      <span class="text-gray-600 text-sm font-medium">{{ pageForm.get('gradeName')?.value }}</span>
      }
      }
    </div>
    } @else {
    <div class="w-full mb-4">
      <label class="block uppercase text-gray-500 text-xs font-bold mb-1">
        Target Audience Roles *
      </label>
      <div class="w-full flex flex-row flex-wrap gap-1">
        @for (item of memberTypesList; track item) {
        <app-btn-chip class="mb-1" [label]="item" (click)="onMemberTypeChange(item)" [readOnly]="false"
          bgColor="bg-gray-200" [selected]="pageForm.get('memberType')?.value.includes(item)"></app-btn-chip>
        }
      </div>
    </div>
    }

    <!-- Notice Status -->
    <div class="w-full mb-6">
      <label class="block uppercase text-gray-500 text-xs font-bold mb-1">
        Notice Status
      </label>
      <select formControlName="noticeStatus" id="noticeStatus" name="noticeStatus"
        class="w-full bg-white text-gray-800 border border-gray-300 rounded-lg px-3 py-2 text-sm shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary-500 capitalize">
        @for (item of noticeStatuses; track $index) {
        <option [value]="item" [selected]="this.pageForm.get('noticeStatus')?.value === item">
          {{item}}
        </option>
        }
      </select>
    </div>

    <!-- Submit Action -->
    <div class="w-full pt-2">
      <app-btn-submit [isDisabled]="isSubmitDisabled()" [isLoading]="working"
        [actionText]="noticeData?.id ? 'Update Notice' : 'Create Notice'" [icon]="'fa fa-save'"></app-btn-submit>
    </div>
  </form>
</div>

<!-- AI Chat Drawer — slides in from the right (see .chat-drawer in the stylesheet), toggled by the
     floating action button below. Stays available on top of both the Notice Details and Notice
     Image tabs, so the user can keep chatting with the AI no matter which one is active. -->
<div class="chat-drawer bg-white border-l border-gray-200 shadow-2xl flex flex-col" [class.open]="chatOpen">
  <ng-container *ngTemplateOutlet="aiChatPanel"></ng-container>
</div>

<button type="button" (click)="toggleChat()" [class.ai-shimmer]="aiGenerating"
  class="ai-fab bg-primary-500 hover:bg-primary-600 text-white border border-white/10">
  @if (chatOpen) {
  <i class="fa fa-xmark"></i>
  } @else {
  <i class="fa fa-wand-magic-sparkles"></i>
  }
</button>

<!-- Reusable AI chat panel template, rendered into the drawer above via *ngTemplateOutlet. -->
<ng-template #aiChatPanel>
  <div class="flex items-center gap-2 px-4 py-3 border-b border-gray-200 shrink-0">
    <i class="fa fa-wand-magic-sparkles text-primary-500"></i>
    <span class="text-sm font-bold text-gray-800">AI Assistant</span>
  </div>

  <div class="flex-1 overflow-y-auto space-y-4 p-4" #chatContainer>
    @if (chatHistory.length === 0) {
    <p class="text-xs text-gray-400 text-center py-6">
      Describe the notice you want, or ask me to tweak the image once it's ready.
    </p>
    }
    @for (msg of chatHistory; track $index) {
    <div class="flex gap-3" [class.justify-end]="msg.role === 'user'">
      @if (msg.role === 'assistant') {
      <div
        class="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center shrink-0 text-xs font-bold shadow-2xs">
        <i class="fa fa-wand-magic-sparkles text-xs"></i>
      </div>
      }
      <div
        [class]="msg.role === 'user'
                        ? 'bg-primary-500 text-white rounded-xl rounded-tr-none px-4 py-2.5 max-w-[85%] text-sm shadow-2xs'
                        : 'bg-white text-gray-800 border border-gray-200 rounded-xl rounded-tl-none p-4 max-w-[85%] text-sm shadow-2xs space-y-3'">
        <p class="whitespace-pre-line">{{ msg.text }}</p>

        @if (msg.noticePreview) {
        <div class="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p class="text-xs font-bold text-gray-800 mb-1 leading-tight">{{ msg.noticePreview.title }}</p>
          <p class="text-[11px] text-gray-500 leading-relaxed">{{ msg.noticePreview.description }}</p>
        </div>
        }

        @if (msg.imageUrl) {
        <div class="max-w-[130px] max-h-[130px] rounded-xl overflow-hidden border border-gray-200 mt-2">
          <img [src]="msg.imageUrl" alt="AI Generated Notice Banner"
            class="w-full h-full max-w-[130px] max-h-[130px] object-cover" />
        </div>
        }

        @if (msg.chatActions?.length) {
        <div class="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
          @for (action of msg.chatActions; track action.type) {
          <button type="button" (click)="handleChatAction(action.type)" [disabled]="working"
            class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all shadow-xs font-medium disabled:opacity-50">
            <i [class]="action.icon + ' text-primary-500 text-[11px]'"></i>
            {{ action.label }}
          </button>
          }
        </div>
        }
      </div>

      @if (msg.role === 'user') {
      <div
        class="w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center shrink-0 text-xs font-bold overflow-hidden shadow-2xs">
        @if (user?.photoURL) {
        <img [src]="user?.photoURL" [alt]="user?.name || 'User'" class="w-full h-full object-cover" />
        } @else {
        <i class="fa fa-user"></i>
        }
      </div>
      }
    </div>
    }

    @if (aiGenerating) {
    <div class="flex gap-3 items-center text-xs text-gray-500">
      <div class="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center animate-pulse">
        <i class="fa fa-spinner fa-spin"></i>
      </div>
      <span>AI is thinking...</span>
    </div>
    }

    @if (aiImageGenerating && !aiGenerating) {
    <div class="flex gap-3 items-center text-xs text-gray-500">
      <div
        class="w-8 h-8 rounded-full bg-primary-500/20 text-primary-600 flex items-center justify-center animate-pulse">
        <i class="fa fa-image text-xs"></i>
      </div>
      <span>Generating notice image...</span>
    </div>
    }
  </div>

  <div class="p-3 border-t border-gray-200 shrink-0 space-y-2">
    <div class="flex flex-wrap items-center gap-2">
      @for (qp of quickPrompts; track qp) {
      <button type="button" (click)="useQuickPrompt(qp)"
        class="text-xs px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-700 transition-all">
        {{ qp }}
      </button>
      }
      @if (hasDraft && !pageForm.invalid && !working) {
      <button type="button" (click)="upsert()" [disabled]="working"
        class="ml-auto flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white shadow-2xs transition-all">
        <i class="fa fa-save"></i>
        {{ noticeData?.id ? 'Save Changes' : 'Save Notice' }}
      </button>
      }
    </div>

    <div class="relative w-full">
      <textarea [(ngModel)]="aiPromptInput" [ngModelOptions]="{standalone: true}" rows="2"
        (keydown.enter)="$event.preventDefault(); submitAiPrompt()"
        placeholder="Describe your notice, ask to change a field or the image, or say 'save it'..."
        [disabled]="aiGenerating"
        class="w-full p-3 pr-20 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 resize-none shadow-2xs"></textarea>

      <div class="absolute right-2 bottom-2 flex items-center gap-1.5">
        <button type="button" (click)="toggleVoiceInput()" [title]="isRecordingVoice ? 'Stop recording' : 'Speak to AI'"
          [class]="isRecordingVoice ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          class="w-8 h-8 rounded-lg flex items-center justify-center transition-all shadow-2xs">
          <i [class]="isRecordingVoice ? 'fa fa-stop text-xs' : 'fa fa-microphone text-xs'"></i>
        </button>

        <button type="button" (click)="submitAiPrompt()" [disabled]="aiGenerating || !aiPromptInput.trim()"
          class="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white font-semibold text-xs rounded-lg transition-all shadow-2xs flex items-center gap-1.5">
          <i class="fa fa-paper-plane"></i>
        </button>
      </div>
    </div>
  </div>
</ng-template>

<!-- Preview Modal — on-demand final look, replaces the old permanent right-side preview column. -->
@if (showPreviewModal) {
<div class="fixed inset-0 z-40 bg-gray-900/60 backdrop-blur-xs flex items-center justify-center p-4"
  (click)="showPreviewModal = false">
  <div (click)="$event.stopPropagation()">
    <div class="flex justify-end mb-2">
      <button type="button" (click)="showPreviewModal = false" class="text-white/80 hover:text-white">
        <i class="fa fa-xmark text-xl"></i>
      </button>
    </div>
    <ng-container *ngTemplateOutlet="mobilePreviewTemplate"></ng-container>
  </div>
</div>
}

<!-- Reusable Mobile Preview Template (Mirrors Mobile Device View with Banner Image & Details) -->
<ng-template #mobilePreviewTemplate>
  <div
    class="w-[340px] sm:w-[370px] bg-gray-950 rounded-[48px] p-3.5 shadow-2xl border-[6px] border-gray-800 relative mx-auto font-sans">

    <!-- Phone Top Notch / Speaker -->
    <div class="w-28 h-4 bg-black rounded-full mx-auto mb-2 flex items-center justify-center gap-2 relative z-30">
      <div class="w-2.5 h-2.5 rounded-full bg-gray-900"></div>
      <div class="w-8 h-1.5 rounded-full bg-gray-900"></div>
    </div>

    <!-- Phone Screen Container -->
    <div
      class="bg-slate-50 rounded-[38px] overflow-hidden min-h-[640px] max-h-[720px] flex flex-col border border-gray-800/40 text-gray-900 shadow-inner relative">

      <!-- Notice Banner Header Section -->
      <div class="relative h-64 sm:h-72 w-full bg-gray-900 shrink-0 overflow-hidden">
        @if (currentNoticeImage) {
        <img [src]="currentNoticeImage" alt="Notice Banner" class="w-full h-full object-cover" />
        } @else {
        <!-- Styled Banner Placeholder when no custom image uploaded -->
        <div
          class="w-full h-full bg-gradient-to-br from-slate-800 via-primary-900 to-indigo-950 flex items-center justify-center p-6 text-center relative">
          <div class="absolute inset-0 bg-[radial-gradient(circle_at_center,white_0%,transparent_100%)] opacity-10">
          </div>
          <div class="space-y-2 z-10">
            <div
              class="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center mx-auto text-white text-xl border border-white/20">
              <i class="fa fa-bullhorn"></i>
            </div>
            <p class="text-xs text-white/70 font-medium tracking-wide">Notice Banner Preview</p>
          </div>
        </div>
        }

        <!-- Mobile Device Status Bar Overlay (Time, Signal, Wifi, Battery) -->
        <div
          class="absolute inset-x-0 top-0 pt-2 px-4 flex items-center justify-between text-white z-20 bg-gradient-to-b from-black/80 via-black/40 to-transparent pb-6 drop-shadow-xs">
          <!-- Time -->
          <span class="text-xs font-bold tracking-tight">12:30</span>

          <!-- Icons (Signal, Wifi, Battery) -->
          <div class="flex items-center gap-1.5 text-xs">
            <i class="fa fa-signal text-[10px]"></i>
            <i class="fa fa-wifi text-[10px]"></i>
            <i class="fa fa-battery-full text-xs"></i>
          </div>
        </div>

        <!-- Back Arrow Button overlay -->
        <div class="absolute top-8 left-4 z-20">
          <button type="button"
            class="w-8 h-8 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center text-white text-sm hover:bg-black/50 transition-all border border-white/10 shadow-xs">
            <i class="fa fa-chevron-left text-xs"></i>
          </button>
        </div>

        <!-- Dark Gradient Overlay & Notice Title Text (Bottom of Banner Image) -->
        <div
          class="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-black/95 via-black/60 to-transparent px-5 pb-4 flex items-end z-10">
          <h2 class="text-xl sm:text-2xl font-bold text-white leading-snug drop-shadow-md font-sans tracking-tight">
            {{ pageForm.get('title')?.value || 'New Members Fellowship Lunch' }}
          </h2>
        </div>
      </div>

      <!-- Scrollable Notice Content Below Banner -->
      <div class="flex-1 overflow-y-auto p-5 space-y-3.5 bg-slate-50">

        <!-- Date Timestamp line (matches attached screenshot: "24 Jul 2026 06:29 PM") -->
        <div class="text-[12px] text-gray-500 font-normal tracking-wide">
          {{ pageForm.get('publishOn')?.value ? (pageForm.get('publishOn')?.value | date:'dd MMM yyyy hh:mm a') : '24
          Jul 2026 06:29 PM' }}
        </div>

        <!-- Description Text Body -->
        <div class="text-sm text-gray-700 leading-relaxed whitespace-pre-line font-normal">
          {{ pageForm.get('description')?.value || 'If you have recently joined our church family, we invite you to a
          fellowship lunch after the second service. Meet the pastors and learn more about our ministries.' }}
        </div>

        <!-- Status & Role Badges -->
        <div class="flex items-center gap-1.5 flex-wrap pt-2">
          <span
            class="text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 capitalize border border-emerald-200">
            {{ pageForm.get('noticeStatus')?.value || 'Active' }}
          </span>
          @if (pageForm.get('isGradeNotice')?.value) {
          <span
            class="text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">
            Grade Notice
          </span>
          }
          @for (role of pageForm.get('memberType')?.value; track role) {
          <span class="text-[10px] font-medium bg-gray-200 text-gray-700 px-2.5 py-0.5 rounded-full capitalize">
            {{ role }}
          </span>
          }
        </div>

        <!-- Attachments Preview List -->
        @if (existingAttachments.length > 0 || newAttachments.length > 0) {
        <div class="space-y-2 pt-3 border-t border-gray-200">
          <p class="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Attachments</p>
          @for (att of existingAttachments; track att.name) {
          <div
            class="flex items-center gap-3 p-2.5 bg-white rounded-xl text-xs text-gray-800 border border-gray-200 shadow-2xs">
            <i [class]="att.url.toLowerCase().endsWith('.pdf') ? 'fa-solid fa-file-pdf text-red-500' : 'fa-solid fa-file text-primary-500'"
              class="text-sm"></i>
            <span class="truncate flex-1 font-medium">{{ att.name }}</span>
          </div>
          }
          @for (att of newAttachments; track att.name) {
          <div
            class="flex items-center gap-3 p-2.5 bg-white rounded-xl text-xs text-gray-800 border border-gray-200 shadow-2xs">
            <i [class]="att.name.toLowerCase().endsWith('.pdf') ? 'fa-solid fa-file-pdf text-red-500' : 'fa-solid fa-file text-primary-500'"
              class="text-sm"></i>
            <span class="truncate flex-1 font-medium">{{ att.name }}</span>
          </div>
          }
        </div>
        }

      </div>

      <!-- Phone Bottom Home Bar Indicator -->
      <div class="py-2.5 bg-slate-50 shrink-0 flex justify-center border-t border-gray-200/40">
        <div class="w-32 h-1 bg-gray-900/80 rounded-full"></div>
      </div>

    </div>
  </div>
</ng-template>

<!-- Save Notice Image Style Popup Dialog Modal -->
@if (showSaveStyleDialog) {
<div class="fixed inset-0 bg-gray-900/60 backdrop-blur-xs flex justify-center items-center z-50 p-4 animate-fade-in">
  <div class="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 relative space-y-5">

    <!-- Dialog Header -->
    <div class="flex items-center justify-between border-b border-gray-200 pb-3">
      <h3 class="text-base font-bold text-gray-900 flex items-center gap-2">
        <i class="fa fa-wand-magic-sparkles text-primary-500"></i> Save Notice Image Style
      </h3>
      <button type="button" (click)="closeSaveStyleDialog()"
        class="text-gray-400 hover:text-gray-600 transition-colors">
        <i class="fa fa-xmark text-lg"></i>
      </button>
    </div>

    <!-- Optional Image Thumbnail Preview -->
    @if (currentNoticeImage) {
    <div class="w-full h-36 rounded-xl overflow-hidden border border-gray-200 shadow-2xs">
      <img [src]="currentNoticeImage" alt="Notice Banner Preview" class="w-full h-full object-cover" />
    </div>
    }

    <p class="text-xs text-gray-600">
      Would you like to save this notice image style to your group's style database for future AI notice generation?
    </p>

    <!-- Style Name Input -->
    <div>
      <label class="block uppercase text-gray-500 text-[11px] font-bold mb-1">
        Style Name *
      </label>
      <input type="text" [(ngModel)]="styleNameInput" [ngModelOptions]="{standalone: true}"
        placeholder="e.g. Youth Event Banner Style..."
        class="w-full bg-white text-gray-800 border border-gray-300 rounded-lg px-3 py-2 text-xs shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
    </div>

    <!-- Dialog Action Buttons -->
    <div class="flex flex-col sm:flex-row items-center justify-end gap-2 pt-2 border-t border-gray-200">
      <button type="button" (click)="closeSaveStyleDialog()" [disabled]="isSavingStyle"
        class="w-full sm:w-auto px-3.5 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 rounded-lg border border-gray-300 transition-colors">
        Skip
      </button>
      <button type="button" (click)="saveNoticeStyle(false)" [disabled]="isSavingStyle || !styleNameInput.trim()"
        class="w-full sm:w-auto px-3.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-xs rounded-lg transition-colors border border-gray-300 flex items-center justify-center gap-1">
        <i class="fa fa-bookmark"></i> Save Style
      </button>
      <button type="button" (click)="saveNoticeStyle(true)" [disabled]="isSavingStyle || !styleNameInput.trim()"
        class="w-full sm:w-auto px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-xs rounded-lg shadow-2xs transition-colors flex items-center justify-center gap-1">
        <i class="fa fa-star"></i> Save as Default Style
      </button>
    </div>

  </div>
</div>
}